/** Image / Asset Detail renderer (VISTOERA_PRODUCTION_CANVAS_NODE_DETAIL_SHELL_AND_IMAGE_ASSET_UI_V2
 * §5–§14) — the first real `NodeDetailRenderer` implementation.
 *
 * Fetches through the existing Image Asset Picker detail endpoint
 * (`getImagePickerItemDetail`, `sa:{entity_id}`) — the same contract the
 * Image Asset Center already uses — instead of inventing a second
 * "canvas asset detail" API. Never invents prompt/candidate data: those
 * two fields come back `null`/`[]` from the backend whenever this
 * asset's generation trail can't be resolved (PROMPT_DATA_GAP, see
 * `image_picker_service._resolve_generation_provenance`'s docstring),
 * and this renderer shows an honest "无可用提示词" hint rather than
 * fabricating one client-side. */

import { CopyOutlined, SwapOutlined } from '@ant-design/icons'
import { Image as AntImage, Skeleton, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImagePickerDetail } from '../../../../api'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import type { NodeDetailRendererProps } from '../types'

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

function aspectRatioLabel(width: number | null, height: number | null): string | null {
  if (!width || !height) return null
  const g = gcd(width, height) || 1
  return `${width / g}:${height / g}`
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch {
    return null
  }
}

export default function ImageAssetDetailRenderer({
  node,
  workspaceId,
  siteId,
  onChangeEntity,
  onOpenAssetPicker,
  onMaterializeImageAssets,
}: NodeDetailRendererProps) {
  const { t } = useTranslation('canvas')
  const [detail, setDetail] = useState<ImagePickerDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [activeCandidateId, setActiveCandidateId] = useState<string | null>(null)

  const sourceKindLabels = useMemo(
    () => ({
      generated: t('detail.sourceKindGenerated'),
      uploaded: t('detail.sourceKindUploaded'),
      asset_library: t('detail.sourceKindLibrary'),
    }),
    [t],
  )

  const statusLabels = useMemo(
    () => ({
      ready: t('detail.statusReady'),
      processing: t('detail.statusProcessing'),
      failed: t('media.failed'),
    }),
    [t],
  )

  const entityId = node.entity_id
  const pickerItemId = entityId ? `sa:${entityId}` : null

  useEffect(() => {
    setActiveCandidateId(null)
    if (!pickerItemId || !workspaceId) {
      setDetail(null)
      setLoadError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    loadImagePickerItemDetailDeduped(workspaceId, pickerItemId, { include_usage: false, include_variants: false })
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('detail.loadImageFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [pickerItemId, workspaceId, t])

  const candidates = detail?.candidates || []
  const activeCandidate = useMemo(() => {
    if (!activeCandidateId) return null
    return candidates.find((c) => c.id === activeCandidateId) || null
  }, [activeCandidateId, candidates])

  const openPicker = () => {
    if (!siteId || !onChangeEntity || !onOpenAssetPicker) return
    if (entityId) {
      onOpenAssetPicker({
        nodeId: node.id,
        purpose: 'graphic_replace',
        title: t('mediaCard.replaceImage'),
        selectionMode: 'single',
        maxSelection: 1,
        onConfirm: (selections) => {
          const first = selections[0]
          if (!first) return
          onChangeEntity(node.id, { entityType: 'site_asset', entityId: first.assetId })
        },
      })
      return
    }
    onOpenAssetPicker({
      nodeId: node.id,
      purpose: 'canvas_insert',
      title: t('mediaCard.selectImage'),
      selectionMode: 'multiple',
      maxSelection: 20,
      onConfirm: (selections) => {
        if (!selections.length) return
        if (onMaterializeImageAssets) {
          onMaterializeImageAssets(selections, { bindNodeId: node.id })
          return
        }
        const first = selections[0]
        if (first) onChangeEntity(node.id, { entityType: 'site_asset', entityId: first.assetId })
      },
    })
  }

  if (!entityId) {
    return (
      <div className="pcv-image-detail pcv-image-detail--empty">
        <div className="pcv-image-detail-placeholder-icon">🖼</div>
        <div className="pcv-image-detail-empty-title">{t('detail.noImageLinked')}</div>
        <div className="pcv-field-hint">{t('detail.noImageLinkedHint')}</div>
        {siteId && onChangeEntity && onOpenAssetPicker ? (
          <button type="button" className="pcv-copy-btn" onClick={openPicker}>
            {t('detail.selectImage')}
          </button>
        ) : null}
      </div>
    )
  }

  if (loading && !detail) {
    return (
      <div className="pcv-image-detail">
        <Skeleton.Image active style={{ width: '100%', height: 200 }} />
        <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ marginTop: 12 }} />
      </div>
    )
  }

  if (loadError || !detail) {
    return <div className="pcv-image-detail pcv-field-hint">{loadError || t('detail.noImageInfo')}</div>
  }

  const previewUrl = activeCandidate ? activeCandidate.thumbnail_url : detail.full_url || detail.preview_url || detail.thumbnail_url
  const sourceKind = detail.source_kind || 'asset_library'
  const sourceLabel = sourceKindLabels[sourceKind as keyof typeof sourceKindLabels] || t('detail.sourceKindLibrary')
  const statusLabel = statusLabels[detail.status as keyof typeof statusLabels] || detail.status
  const ratio = aspectRatioLabel(detail.width, detail.height)
  const createdLabel = formatDateTime(detail.created_at)

  const handleCopyPrompt = () => {
    if (!detail.prompt) return
    void navigator.clipboard
      ?.writeText(detail.prompt)
      .then(() => message.success(t('detail.promptCopied')))
      .catch(() => message.error(t('detail.copyFailed')))
  }

  return (
    <div className="pcv-image-detail">
      <div className="pcv-image-detail-preview">
        {previewUrl ? (
          <AntImage src={previewUrl} alt="" classNames={{ root: 'pcv-image-detail-preview-img' }} />
        ) : (
          <div className="pcv-image-detail-placeholder-icon">🖼</div>
        )}
      </div>

      {siteId && onChangeEntity && onOpenAssetPicker ? (
        <button type="button" className="pcv-copy-btn pcv-image-detail-swap-btn" onClick={openPicker}>
          <SwapOutlined /> {t('detail.swapImage')}
        </button>
      ) : null}

      {candidates.length > 1 ? (
        <div className="pcv-image-detail-candidates">
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`pcv-image-candidate-thumb${(activeCandidateId ?? (c.is_current ? c.id : null)) === c.id ? ' is-active' : ''}`}
              onClick={() => setActiveCandidateId(c.id)}
              title={c.is_current ? t('detail.currentCandidate') : t('detail.candidate')}
              aria-label={c.is_current ? t('detail.currentCandidate') : t('detail.candidate')}
            >
              {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" /> : null}
            </button>
          ))}
        </div>
      ) : null}

      <div className="pcv-detail-section">
        <label className="pcv-field-label">{t('detail.sourceMethod')}</label>
        <div className="pcv-detail-value">{sourceLabel}</div>
      </div>

      {sourceKind === 'uploaded' ? (
        <div className="pcv-detail-section">
          <label className="pcv-field-label">{t('detail.filename')}</label>
          <div className="pcv-detail-value">{detail.filename}</div>
        </div>
      ) : null}

      {sourceKind === 'asset_library' ? (
        <div className="pcv-detail-section">
          <label className="pcv-field-label">{t('detail.assetName')}</label>
          <div className="pcv-detail-value">{detail.filename}</div>
        </div>
      ) : null}

      <div className="pcv-detail-section">
        <div className="pcv-detail-section-header">
          <label className="pcv-field-label">{t('detail.originalPrompt')}</label>
          {detail.prompt ? (
            <button type="button" className="pcv-copy-btn" onClick={handleCopyPrompt} title={t('detail.copyPrompt')} aria-label={t('detail.copyPrompt')}>
              <CopyOutlined /> {t('common.copy')}
            </button>
          ) : null}
        </div>
        <div className="pcv-detail-value pcv-detail-value--prompt">{detail.prompt || t('detail.noPrompt')}</div>
      </div>

      <div className="pcv-detail-section">
        <label className="pcv-field-label">{t('detail.imageInfo')}</label>
        <div className="pcv-image-info-grid">
          {detail.width && detail.height ? (
            <div className="pcv-image-info-cell">
              <span className="pcv-image-info-value">
                {detail.width} × {detail.height}
              </span>
              <span className="pcv-image-info-key">{t('detail.dimensions')}</span>
            </div>
          ) : null}
          {ratio ? (
            <div className="pcv-image-info-cell">
              <span className="pcv-image-info-value">{ratio}</span>
              <span className="pcv-image-info-key">{t('detail.aspectRatio')}</span>
            </div>
          ) : null}
          {candidates.length > 1 ? (
            <div className="pcv-image-info-cell">
              <span className="pcv-image-info-value">{t('detail.candidateCount', { count: candidates.length })}</span>
              <span className="pcv-image-info-key">{t('detail.candidates')}</span>
            </div>
          ) : null}
          <div className="pcv-image-info-cell">
            <span className="pcv-image-info-value">{statusLabel}</span>
            <span className="pcv-image-info-key">{t('detail.status')}</span>
          </div>
          {createdLabel ? (
            <div className="pcv-image-info-cell">
              <span className="pcv-image-info-value">{createdLabel}</span>
              <span className="pcv-image-info-key">{t('detail.createdAt')}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
